import React, { useState, useEffect, useCallback } from "react";
import {
    Modal,
    Upload,
    Button,
    App,
    Table,
    Typography,
    Space,
} from "antd";
import {
    UploadOutlined,
    DownloadOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import API from "API";

const { Text } = Typography;

const BaseExcelUploadModal = ({
    visible,
    onClose,
    onImportSuccess,
    apiPath = "",
    itemName = "mục",
    rowKey = "id",
    additionalPostData = {},
    afterCreated = null,
    resetOnClose = false,
    expectedCsvType = "",
    onReloadList = null,
}) => {
    const [uploading, setUploading] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [fileList, setFileList] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [templateHeaders, setTemplateHeaders] = useState([]);
    const [templateBlob, setTemplateBlob] = useState(null);
    const { message } = App.useApp();

    const fetchTemplateHeaders = useCallback(async () => {
        if (!expectedCsvType) return;
        try {
            const res = await API.get(`/${expectedCsvType}/csv_file/`, { responseType: "blob" });
            const blob = res.data;
            const arrayBuffer = await blob.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const headers = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] || [];

            console.log("[Template] Loaded headers:", headers);

            setTemplateHeaders(headers);
            setTemplateBlob(blob);
        } catch (error) {
            console.error("Không thể tải hoặc đọc file mẫu:", error);
            message.error("Không thể tải file mẫu.");
        }
    }, [expectedCsvType, message]);

    useEffect(() => {
        if (visible) {
            fetchTemplateHeaders();
        }
        if (!visible && resetOnClose) {
            handleRemoveFile();
        }
    }, [visible, resetOnClose, fetchTemplateHeaders]);

    const handleDownloadTemplate = () => {
        if (!templateBlob) {
            message.error("Chưa tải xong file mẫu.");
            return;
        }
        const url = window.URL.createObjectURL(templateBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${expectedCsvType}_template.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleRemoveFile = () => {
        setFileList([]);
        setPreviewData([]);
        setShowAll(false);
    };

    const handleFileUpload = async (file) => {
        if (!file) return false;
        setUploading(true);
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const arrayBuffer = e.target.result;
                const workbook = XLSX.read(arrayBuffer, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const sheetHeaders = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] || [];
                const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });

                console.log("[Upload] Headers:", sheetHeaders);

                if (templateHeaders.length === 0) {
                    console.warn("[Upload] No template headers loaded yet.");
                    message.error("Chưa có mẫu để kiểm tra.");
                    handleRemoveFile();
                    return;
                }

                const isHeaderMismatch =
                    sheetHeaders.length !== templateHeaders.length ||
                    !templateHeaders.every((h, i) => h === sheetHeaders[i]);

                if (isHeaderMismatch) {
                    message.error(`File không đúng định dạng dành cho ${itemName}.`);
                    handleRemoveFile();
                    return;
                }

                if (data.length === 0) {
                    message.warning("File không có dữ liệu.");
                } else {
                    setFileList([file]);
                    setPreviewData(data);
                    setShowAll(false);
                    message.success("Đã đọc file thành công.");
                }
            };
            reader.readAsArrayBuffer(file);
        } catch (err) {
            console.error(err);
            message.error("Lỗi khi xử lý file.");
        } finally {
            setUploading(false);
        }
        return false;
    };

    const handleConfirmImport = async () => {
        if (!previewData.length) {
            message.warning("Không có dữ liệu để import.");
            return;
        }
        setUploading(true);
        try {
            const results = await Promise.all(
                previewData.map((item) => API.post(apiPath, { ...item, ...additionalPostData }))
            );

            if (afterCreated) {
                await Promise.all(results.map((res) => afterCreated(res)));
            }

            message.success(`Đã import ${itemName} thành công!`);
            onClose?.();
            onImportSuccess?.();
            onReloadList?.();
        } catch (err) {
            console.error(err);
            message.error(`Lỗi khi import ${itemName}.`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            title={`Import ${itemName} từ CSV`}
            footer={null}
            width={720}
            destroyOnClose
        >
            <Space direction="vertical" style={{ width: "100%" }}>
                {expectedCsvType && (
                    <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                        Tải file mẫu CSV
                    </Button>
                )}

                {fileList.length === 0 ? (
                    <Upload
                        accept=".csv"
                        maxCount={1}
                        beforeUpload={handleFileUpload}
                        showUploadList={false}
                    >
                        <Button icon={<UploadOutlined />} loading={uploading}>
                            Chọn file CSV để upload
                        </Button>
                    </Upload>
                ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Text>📄 {fileList[0].name}</Text>
                        <Button type="link" icon={<DeleteOutlined />} onClick={handleRemoveFile} danger>
                            Xóa file
                        </Button>
                    </div>
                )}

                {previewData.length > 0 && (
                    <>
                        <Table
                            columns={(templateHeaders.length > 0
                                ? templateHeaders
                                : Object.keys(previewData[0] || {})
                            ).map((h) => ({
                                title: h,
                                dataIndex: h,
                                key: h,
                            }))}
                            dataSource={
                                previewData.length <= 5
                                    ? previewData
                                    : (showAll ? previewData.slice(0, 10) : previewData.slice(0, 5))
                            }
                            rowKey={rowKey}
                            pagination={false}
                            size="small"
                            style={{ marginTop: 16, maxHeight: 300, overflowY: "auto" }}
                        />
                        {previewData.length > 5 && (
                            <Button
                                type="link"
                                onClick={() => setShowAll((prev) => !prev)}
                                style={{ paddingLeft: 0 }}
                            >
                                {showAll
                                    ? `🔼 Thu gọn về 5 dòng đầu tiên`
                                    : `👁 Xem tối đa 10 dòng (${previewData.length} dòng trong file)`}
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={handleConfirmImport}
                            loading={uploading}
                            style={{ marginTop: 16 }}
                            block
                        >
                            Xác nhận import
                        </Button>
                    </>
                )}
            </Space>
        </Modal>
    );
};

export default BaseExcelUploadModal;
